import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, ArrowRight } from "lucide-react";
import { api } from "../lib/api";

/**
 * Player of the Game spotlight — shows the top scorer from the most recent
 * final game (with a saved box score). Hides itself if no eligible game.
 */
export default function PlayerOfTheGame() {
    const [data, setData] = useState(undefined); // undefined = loading, null = no data

    useEffect(() => {
        api.get("/player-of-the-game")
            .then((r) => setData(r.data || null))
            .catch(() => setData(null));
    }, []);

    if (data === undefined || data === null) return null;
    const { game, player, stat_line } = data;
    const photo = player?.photo_url || stat_line?.photo_url;
    const name = stat_line?.player_name || player?.name || "Purple Kings player";
    const number = stat_line?.jersey_number || player?.jersey_number;
    const position = player?.position;
    const result = game?.result || (
        game?.our_score != null && game?.opp_score != null
            ? `${game.our_score > game.opp_score ? "W" : "L"} ${game.our_score}-${game.opp_score}`
            : ""
    );
    const gameDate = game?.date ? new Date(game.date).toLocaleDateString("en-US", {
        month: "short", day: "numeric", year: "numeric",
    }) : "";

    const stats = [
        { label: "PTS", value: stat_line?.points ?? 0 },
        { label: "REB", value: stat_line?.rebounds ?? 0 },
        { label: "AST", value: stat_line?.assists ?? 0 },
        { label: "STL", value: stat_line?.steals ?? 0 },
        { label: "BLK", value: stat_line?.blocks ?? 0 },
    ];

    return (
        <section
            className="relative border-y border-line overflow-hidden"
            data-testid="player-of-the-game"
        >
            {/* Decorative purple → gold gradient backdrop */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-dark via-bg to-bg" />
            <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-gold/10 blur-3xl" />
            <div className="absolute -bottom-20 -left-20 w-96 h-96 rounded-full bg-purple/20 blur-3xl" />

            <div className="relative max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-24">
                <div className="grid md:grid-cols-12 gap-8 md:gap-10 items-center">
                    {/* Photo + crown */}
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                        className="md:col-span-5 relative"
                    >
                        <div className="absolute -top-6 -left-2 z-10 flex items-center gap-2 px-3 py-1.5 bg-gold text-bg text-[10px] tracking-[0.3em] uppercase font-black shadow-lg">
                            <Crown size={14} /> PrimeWay Top Hooper of the Game
                        </div>
                        <div
                            className="aspect-[4/5] w-full max-w-md mx-auto bg-cover bg-center border border-gold/40 shadow-2xl"
                            style={{
                                backgroundImage: photo ? `url('${photo}')` : "linear-gradient(135deg, #4a1d6e, #1a0a2e)",
                                backgroundColor: "#1a0a2e",
                            }}
                            data-testid="potg-photo"
                        />
                    </motion.div>

                    {/* Right side: name + stat line + meta */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.1 }}
                        className="md:col-span-7"
                    >
                        <div className="text-gold text-[10px] tracking-[0.4em] uppercase font-bold mb-3 flex items-center gap-3">
                            <span className="h-px w-12 bg-gold" />
                            Most Recent Game{gameDate ? ` · ${gameDate}` : ""}
                        </div>

                        <h2
                            className="h-display text-5xl md:text-7xl text-white leading-[0.9]"
                            data-testid="potg-name"
                        >
                            {name}
                        </h2>

                        <div className="mt-3 text-white/70 text-base flex flex-wrap gap-x-4 gap-y-1">
                            {number && <span className="text-gold font-bold">#{number}</span>}
                            {position && <span>{position}</span>}
                            {game?.opponent && (
                                <span>
                                    vs. <span className="text-white">{game.opponent}</span>
                                    {result && (
                                        <span className={`ml-2 font-bold ${result.startsWith("W") ? "text-green-400" : "text-red-400"}`}>
                                            {result}
                                        </span>
                                    )}
                                </span>
                            )}
                        </div>

                        {/* Stat line */}
                        <div
                            className="mt-7 grid grid-cols-5 gap-px bg-line border border-line"
                            data-testid="potg-stats"
                        >
                            {stats.map((s) => (
                                <div key={s.label} className="bg-bg-soft px-3 py-5 text-center">
                                    <div className="h-display text-3xl md:text-4xl text-gold leading-none">
                                        {s.value}
                                    </div>
                                    <div className="text-[10px] tracking-[0.3em] uppercase text-ink mt-2">
                                        {s.label}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-7 flex flex-wrap items-center gap-4">
                            <Link
                                to="/schedule"
                                data-testid="potg-box-score-link"
                                className="inline-flex items-center gap-2 text-gold text-xs tracking-[0.3em] uppercase font-bold hover:text-gold-bright"
                            >
                                Full Box Score <ArrowRight size={14} />
                            </Link>
                            {player?.id && (
                                <Link
                                    to="/roster"
                                    className="inline-flex items-center gap-2 text-white/70 text-xs tracking-[0.3em] uppercase font-bold hover:text-white"
                                >
                                    Roster <ArrowRight size={14} />
                                </Link>
                            )}
                        </div>

                        {/* PrimeWay Federal Credit Union sponsor block */}
                        <a
                            href="https://www.primewayfcu.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="potg-primeway-cta"
                            className="group mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 p-5 bg-white/95 hover:bg-white transition-colors border border-gold/50 shadow-lg"
                        >
                            <img
                                src="/api/files/sponsors/primeway-fcu-logo.png"
                                alt="PrimeWay Federal Credit Union"
                                className="h-14 md:h-16 w-auto shrink-0"
                                data-testid="potg-primeway-logo"
                            />
                            <div className="flex-1 min-w-0">
                                <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 font-bold">
                                    Presented By
                                </div>
                                <div className="text-neutral-900 font-bold text-sm md:text-base mt-1 leading-snug">
                                    Start your new financial journey with PrimeWay FCU
                                </div>
                                <div className="mt-1 text-xs md:text-sm text-neutral-600 group-hover:text-neutral-800 inline-flex items-center gap-1 tracking-wide">
                                    www.primewayfcu.com <ArrowRight size={13} className="group-hover:translate-x-0.5 transition-transform" />
                                </div>
                            </div>
                        </a>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
